/**
 * Checks & Traites dashboard widgets.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { CheckDraft } from '@erp/data-access/interfaces';
import { useChecksDrafts, useChecksDueSoon } from '@/hooks/use-checks';
import { useClients } from '@/hooks/use-clients';
import { useSuppliers } from '@/hooks/use-suppliers';
import { useBankAccounts } from '@/hooks/use-treasury';
import { getDueDateStatus, daysUntilDue } from '@erp/domain/checks';
import {
  Badge, Button,
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@erp/ui';
import { Widget, BigStat, fmtMoney, fmtCount, fmtDateFr } from './common';
import { ChevronDown, ChevronRight } from '@/components/icons';

// ---- Portfolio value (received items in_wallet) ----

export function PortfolioValueWidget() {
  const { t } = useTranslation();
  const { data: items, isLoading } = useChecksDrafts({ direction: 'received' });

  const stat = useMemo(() => {
    const inWallet = (items ?? []).filter((i) => i.currentState === 'in_wallet');
    return {
      count: inWallet.length,
      total: inWallet.reduce((s, i) => s + i.amount, 0),
    };
  }, [items]);

  return (
    <Widget
      title={t('dashboard.portfolioValue')}
      description={t('dashboard.portfolioHelp')}
      isLoading={isLoading}
    >
      <BigStat
        value={fmtMoney(stat.total, 3)}
        unit="DT"
        sub={t('dashboard.docsCount', { count: stat.count })}
      />
    </Widget>
  );
}

// ---- Pending issued (checks drawn, not yet debited) ----

export function PendingIssuedWidget() {
  const { t } = useTranslation();
  const { data: items, isLoading } = useChecksDrafts({ direction: 'issued' });

  const stat = useMemo(() => {
    const terminal = new Set(['debited', 'rejected']);
    const pending = (items ?? []).filter((i) => !terminal.has(i.currentState));
    return {
      count: pending.length,
      total: pending.reduce((s, i) => s + i.amount, 0),
    };
  }, [items]);

  return (
    <Widget title={t('dashboard.pendingIssued')} description={t('dashboard.pendingIssuedHelp')} isLoading={isLoading}>
      <BigStat
        value={fmtMoney(stat.total, 3)}
        unit="DT"
        sub={t('dashboard.docsCount', { count: stat.count })}
        tone={stat.count > 0 ? 'warning' : 'neutral'}
      />
    </Widget>
  );
}

// ---- Due today or overdue (count + total) ----

export function OverdueChecksWidget() {
  const { t } = useTranslation();
  const { data: items, isLoading } = useChecksDueSoon();

  const stat = useMemo(() => {
    let count = 0;
    let total = 0;
    for (const i of items ?? []) {
      const status = getDueDateStatus(i.dueDate);
      if (status === 'overdue' || daysUntilDue(i.dueDate) === 0) {
        count++;
        total += i.amount;
      }
    }
    return { count, total };
  }, [items]);

  return (
    <Widget
      title={t('dashboard.overdueChecks')}
      description={t('dashboard.overdueChecksHelp')}
      isLoading={isLoading}
    >
      <BigStat
        value={fmtCount(stat.count)}
        sub={stat.count > 0 ? `${fmtMoney(stat.total, 3)} DT` : t('dashboard.noneDueToday')}
        tone={stat.count > 0 ? 'negative' : 'positive'}
      />
    </Widget>
  );
}

// ---- Due soon (1..7 days) ----

export function DueSoonChecksWidget() {
  const { t } = useTranslation();
  const { data: items, isLoading } = useChecksDueSoon();

  const stat = useMemo(() => {
    let count = 0;
    let total = 0;
    for (const i of items ?? []) {
      const days = daysUntilDue(i.dueDate);
      const status = getDueDateStatus(i.dueDate);
      if (status === 'due_soon' && days > 0) {
        count++;
        total += i.amount;
      }
    }
    return { count, total };
  }, [items]);

  return (
    <Widget
      title={t('dashboard.dueSoonChecks')}
      description={t('dashboard.dueSoonChecksHelp')}
      isLoading={isLoading}
    >
      <BigStat
        value={fmtCount(stat.count)}
        sub={stat.count > 0 ? `${fmtMoney(stat.total, 3)} DT` : t('dashboard.noneDueSoon')}
        tone={stat.count > 0 ? 'warning' : 'neutral'}
      />
    </Widget>
  );
}

// ---- Traites grouped by month (issued + received, deep-links to drawer) ----

interface MonthGroup {
  key: string;       // 'YYYY-MM'
  label: string;     // localized "avril 2026"
  issued: TraiteRow[];
  received: TraiteRow[];
}

interface TraiteRow {
  id: string;
  number: string;
  dueDate: string;
  amount: number;
  thirdPartyName: string;
  direction: 'issued' | 'received';
}

export function TraitesByMonthWidget() {
  const { t, i18n } = useTranslation();
  const { data: issued = [], isLoading: loadingIssued } = useChecksDrafts({ type: 'draft', direction: 'issued' });
  const { data: received = [], isLoading: loadingReceived } = useChecksDrafts({ type: 'draft', direction: 'received' });
  const { data: clients = [] } = useClients();
  const { data: suppliers = [] } = useSuppliers();
  const { data: bankAccounts = [] } = useBankAccounts();
  const isLoading = loadingIssued || loadingReceived;

  // Map pour retrouver le CheckDraft complet depuis la liste cliquée.
  const draftById = useMemo(() => {
    const m = new Map<string, CheckDraft>();
    [...issued, ...received].forEach((d) => m.set(d.id, d));
    return m;
  }, [issued, received]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = selectedId ? draftById.get(selectedId) ?? null : null;

  const groups = useMemo<MonthGroup[]>(() => {
    const map = new Map<string, MonthGroup>();
    const TERMINAL = new Set(['debited', 'cashed', 'rejected']);
    const fmt = new Intl.DateTimeFormat(i18n.language || 'fr', { month: 'long', year: 'numeric' });
    const pushItem = (list: typeof issued, dir: 'issued' | 'received') => {
      for (const it of list) {
        if (TERMINAL.has(it.currentState)) continue;
        const key = (it.dueDate ?? '').slice(0, 7);
        if (!key) continue;
        let g = map.get(key);
        if (!g) {
          const [y, m] = key.split('-').map(Number);
          const label = y && m ? fmt.format(new Date(y, m - 1, 1)) : key;
          g = { key, label, issued: [], received: [] };
          map.set(key, g);
        }
        const row: TraiteRow = {
          id: it.id,
          number: it.number,
          dueDate: it.dueDate,
          amount: it.amount,
          thirdPartyName: it.beneficiaryName ?? '—',
          direction: dir,
        };
        (dir === 'issued' ? g.issued : g.received).push(row);
      }
    };
    pushItem(issued, 'issued');
    pushItem(received, 'received');
    // Sort: oldest month first, items inside by due date asc
    const sorted = [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
    sorted.forEach((g) => {
      g.issued.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
      g.received.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    });
    return sorted;
  }, [issued, received, i18n.language]);

  const nowMonthKey = new Date().toISOString().slice(0, 7);
  const [openKeys, setOpenKeys] = useState<Set<string>>(() => new Set([nowMonthKey]));
  const toggle = (key: string) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectedBankAccount = selected?.bankAccountId
    ? bankAccounts.find((b) => b.id === selected.bankAccountId) ?? null
    : null;
  const selectedThirdPartyName = selected
    ? selected.thirdPartyType === 'client'
      ? clients.find((c) => c.id === selected.thirdPartyId)?.name ?? '—'
      : suppliers.find((s) => s.id === selected.thirdPartyId)?.name ?? '—'
    : '—';

  return (
    <>
      <Widget
        title={t('dashboard.traitesByMonth')}
        description={t('dashboard.traitesByMonthHelp')}
        isLoading={isLoading}
        isEmpty={!isLoading && groups.length === 0}
        emptyLabel={t('dashboard.noPendingTraites')}
      >
        <div className="space-y-2 max-h-[420px] overflow-y-auto -mx-1 px-1">
          {groups.map((g) => {
            const isOpen = openKeys.has(g.key);
            const totalIssued = g.issued.reduce((s, r) => s + r.amount, 0);
            const totalReceived = g.received.reduce((s, r) => s + r.amount, 0);
            return (
              <div key={g.key} className="rounded-md border">
                <button
                  type="button"
                  onClick={() => toggle(g.key)}
                  className="w-full flex items-center justify-between gap-2 px-2 sm:px-3 py-2 text-sm font-medium hover:bg-muted/40"
                >
                  <span className="flex items-center gap-2 capitalize min-w-0">
                    {isOpen ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0 rtl:rotate-180" />}
                    <span className="truncate">{g.label}</span>
                  </span>
                  <span className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-xs font-normal tabular-nums shrink-0">
                    {totalIssued > 0 && (
                      <span className="text-rose-600 whitespace-nowrap">
                        − {fmtMoney(totalIssued, 3)}
                      </span>
                    )}
                    {totalReceived > 0 && (
                      <span className="text-emerald-600 whitespace-nowrap">
                        + {fmtMoney(totalReceived, 3)}
                      </span>
                    )}
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t grid grid-cols-1 md:grid-cols-2 md:divide-x divide-y md:divide-y-0">
                    {g.issued.length > 0 && (
                      <TraiteGroupSection
                        label={t('dashboard.traitesToDebit')}
                        tone="negative"
                        rows={g.issued}
                        onRowClick={setSelectedId}
                      />
                    )}
                    {g.received.length > 0 && (
                      <TraiteGroupSection
                        label={t('dashboard.traitesToCollect')}
                        tone="positive"
                        rows={g.received}
                        onRowClick={setSelectedId}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Widget>

      <TraiteQuickViewDialog
        open={!!selected}
        onOpenChange={(v) => { if (!v) setSelectedId(null); }}
        item={selected}
        thirdPartyName={selectedThirdPartyName}
        bankAccountName={selectedBankAccount?.bankName ?? null}
      />
    </>
  );
}

function TraiteGroupSection({
  label,
  tone,
  rows,
  onRowClick,
}: {
  label: string;
  tone: 'negative' | 'positive';
  rows: TraiteRow[];
  onRowClick: (id: string) => void;
}) {
  const toneClass = tone === 'negative' ? 'text-rose-600' : 'text-emerald-600';
  const sign = tone === 'negative' ? '−' : '+';
  return (
    <div className="px-2 sm:px-3 py-2">
      <p className={`text-[11px] uppercase tracking-wide font-semibold mb-1.5 ${toneClass}`}>
        {label}
      </p>
      <ul className="space-y-1">
        {rows.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => onRowClick(r.id)}
              className="w-full flex items-center justify-between gap-2 py-1 px-1.5 rounded hover:bg-muted/60 text-sm text-start"
            >
              <span className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                  {fmtDateFr(r.dueDate)}
                </span>
                <span className="truncate">{r.thirdPartyName}</span>
              </span>
              <span className={`tabular-nums font-medium shrink-0 text-sm whitespace-nowrap ${toneClass}`}>
                {sign} {fmtMoney(r.amount, 3)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Vue rapide d'une traite depuis le tableau de bord — lecture seule,
 * avec un bouton "Ouvrir la fiche" qui route vers /traites pour les
 * actions avancées (transitions d'état, impression, etc.).
 */
function TraiteQuickViewDialog({
  open,
  onOpenChange,
  item,
  thirdPartyName,
  bankAccountName,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  item: CheckDraft | null;
  thirdPartyName: string;
  bankAccountName: string | null;
}) {
  const { t } = useTranslation();
  if (!item) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent />
      </Dialog>
    );
  }
  const directionLabel = t(`checks.${item.direction}`);
  const stateLabel = t(`checks.${item.currentState}`);
  const toneClass = item.direction === 'issued' ? 'text-rose-600' : 'text-emerald-600';
  const sign = item.direction === 'issued' ? '−' : '+';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-mono text-base break-all">{item.number}</DialogTitle>
          <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
            <Badge variant="outline">{directionLabel}</Badge>
            <Badge variant="outline">{stateLabel}</Badge>
          </div>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('checks.issueDate')} value={fmtDateFr(item.issueDate)} />
            <Field label={t('checks.dueDate')} value={fmtDateFr(item.dueDate)} />
          </div>

          <Field
            label={item.thirdPartyType === 'client' ? t('checks.client') : t('checks.supplierLabel')}
            value={thirdPartyName}
          />

          <div>
            <p className="text-xs text-muted-foreground mb-0.5">{t('checks.amount')}</p>
            <p className={`text-2xl font-semibold tabular-nums ${toneClass}`}>
              {sign} {fmtMoney(item.amount, 3)} <span className="text-sm font-normal text-muted-foreground">DT</span>
            </p>
          </div>

          {bankAccountName && (
            <Field label={t('checks.bankAccountIssued')} value={bankAccountName} />
          )}

          {item.beneficiaryName && (
            <Field label={t('checks.beneficiary')} value={item.beneficiaryName} />
          )}

          {item.notes && (
            <Field label={t('checks.notes')} value={item.notes} />
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
          <Button asChild>
            <Link to={`/traites?tab=${item.direction}&open=${item.id}`}>
              {t('dashboard.openFullView')}
            </Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="break-words">{value}</p>
    </div>
  );
}
